import { motion } from "framer-motion";

/**
 * Live scarcity strip for the Hero — pulls from /api/founding-stats.
 * Messaging shifts as the cohort fills so it never reads stale.
 */
export default function FoundingScarcityStrip({ stats }) {
  const claimed = stats?.founders_claimed ?? 0;
  const total = stats?.founders_total ?? 45;
  const left = Math.max(0, total - claimed);
  const foundersClosed = !!stats?.founders_closed;
  const allClosed = !!stats?.all_closed;
  const passesLeft = Math.max(0, (stats?.passes_total ?? 55) - (stats?.passes_claimed ?? 0));

  // Don't render until first fetch so we don't flash "30 left" then drop
  if (!stats?.loaded) return <div className="h-[52px]" aria-hidden />;

  let label;
  let tone = "calm"; // calm | warm | done
  if (allClosed) {
    label = "The founding 100 is complete — thank you.";
    tone = "done";
  } else if (foundersClosed) {
    label = `Free seats taken · ${passesLeft} Matcha Pass spots still open`;
    tone = "warm";
  } else if (left <= 5) {
    label = `Almost full · only ${left} free seat${left === 1 ? "" : "s"} left`;
    tone = "warm";
  } else if (claimed === 0) {
    label = `${total} founding seats just opened — first cup on us`;
    tone = "calm";
  } else {
    label = `${claimed} of ${total} founding seats taken · ${left} left`;
    tone = "calm";
  }

  const fillPct = total === 0 ? 0 : Math.min(100, Math.round((claimed / total) * 100));
  const accent = tone === "warm" ? "#C47253" : tone === "done" ? "#5C7365" : "#2C4C3B";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="mt-7 inline-flex flex-col gap-2 max-w-md w-full"
      data-testid="hero-scarcity-strip"
    >
      <div className="inline-flex items-center gap-2.5 self-start rounded-full bg-white/70 backdrop-blur border border-border px-4 py-2">
        <PulseDot color={accent} muted={tone === "done"} />
        <span
          className="text-[11px] sm:text-xs tracking-[0.14em] uppercase text-[#1A2B22]"
          data-testid="hero-scarcity-label"
        >
          {label}
        </span>
      </div>
      <div
        className="h-[3px] w-full rounded-full bg-[#EBEFDE] overflow-hidden"
        role="progressbar"
        aria-valuenow={claimed}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${claimed} of ${total} founding seats taken`}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${fillPct}%` }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          style={{ background: accent }}
          className="h-full rounded-full"
        />
      </div>
    </motion.div>
  );
}

function PulseDot({ color, muted }) {
  return (
    <span className="relative inline-flex w-2 h-2" aria-hidden>
      {!muted && (
        <span
          className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping"
          style={{ background: color }}
        />
      )}
      <span
        className="relative inline-flex w-2 h-2 rounded-full"
        style={{ background: color }}
      />
    </span>
  );
}
